import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { showToast } from 'shared/ui'
import { TableStore } from 'shared/ui/TableNew'
import { RequestLoadData } from 'shared/api/lib'
import { tableLimitStore } from 'entities/TableLimit'
import { CreditUsageAnalyticsApi } from 'entities/Analytics/api/creditUsageAnalytics'
import {
  type IParamsCreditUsageSourcesExportBase,
  type IParamsCreditUsageSourcesList,
} from 'entities/Analytics/api/types'
import { Source } from 'entities/Analytics/model/Source'
import { RowSource } from 'entities/Analytics/model/Table/RowSource'

export class SourcesTableStore {
  private readonly _tableStore: TableStore<RowSource>
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _items: Source[] = []

  constructor(
    private _analyticsFiltersParams: () => Pick<
      IParamsCreditUsageSourcesList,
      'from' | 'to' | 'timezone'
    >,
    private _selectedSourceGroups: () => string[]
  ) {
    this._tableStore = new TableStore<RowSource>({
      key: 'creditUsageSourcesTable',
      limit: 10,
      getLimit: () => tableLimitStore.getLimit('credit-usage-sources-table'),
      setLimit: (value) => tableLimitStore.setLimit('credit-usage-sources-table', value),
    })

    makeAutoObservable(this)
  }

  init = () => {
    this.loadItems()
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this._tableStore.setLoading(true)
        this._tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => {
        this._requestLoadItems.debounceLoadData()
      }
    )
  }

  get tableStore() {
    return this._tableStore
  }

  get rows() {
    return this._items.map((item) => new RowSource(item))
  }

  get paramsWithoutPage(): Omit<IParamsCreditUsageSourcesList, 'page'> {
    const { from, to, timezone } = this._analyticsFiltersParams()
    const sourceGroup = this._selectedSourceGroups()
    const sortBy = this._tableStore.sortBy

    return {
      from,
      to,
      timezone,
      limit: this._tableStore.limit,
      search_term: this._tableStore.search || undefined,
      sort_by: sortBy || undefined,
      sort_order: sortBy ? this._tableStore.sortOrder : undefined,
      ...(sourceGroup.length ? { source_group: sourceGroup } : {}),
    }
  }

  get paramsGetItems(): IParamsCreditUsageSourcesList {
    return {
      ...this.paramsWithoutPage,
      page: this._tableStore.page,
    }
  }

  get exportParams(): IParamsCreditUsageSourcesExportBase {
    const { from, to, timezone, search_term, sort_by, sort_order, source_group } =
      this.paramsWithoutPage

    return { from, to, timezone, search_term, sort_by, sort_order, source_group }
  }

  setSearch = (value: string) => {
    this._tableStore.setSearch(value)
  }

  private _requestLoadItems = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      this._tableStore.setLoading(true)

      const {
        data: { data, meta },
      } = await CreditUsageAnalyticsApi.getCreditUsageSourcesList(this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })

      runInAction(() => {
        this._items = data.map((item) => new Source(item))
      })

      this._tableStore.setTotal(meta.total)
    },
    errorCallback: () => {
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    },
    finallyCallback: () => {
      this._tableStore.setLoading(false)
    },
  })

  loadItems = async () => {
    return this._requestLoadItems.loadData()
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadItems.dispose()
    this._tableStore.dispose()
  }
}
